import React, { useState, useEffect, useMemo, useCallback } from "react";
import { Search, Book, Shuffle, Music } from "lucide-react";
import jsPDF from "jspdf";

// Import utilities and hooks from shared package
import {
  countSyllables,
  calculateReadingLevel,
  calculateVocabularyComplexity,
  analyzeRhymeStatistics,
  songVocabularyPhoneticMap,
  authService,
  useFileUpload,
  useSearch,
  useAuth,
  AuthProvider,
  useScrollDirection,
  useScrollGlow,
  useSwipeGestures,
  useParticles,
  useSongManager,
  useAudioManager,
  useAPISearch,
  useWordSearch,
  useNotepadEditor,
  useOrganization,
  useVersionHistory,
  useAudioRecorder,
  useTakeManager,
  useTeleprompter,
  downloadFile,
  TeleprompterView,
  isCapacitorApp,
} from "@lyrical-toolkit/shared";

// Import components
import { Header } from "@lyrical-toolkit/shared";
import { BottomNav } from "@lyrical-toolkit/shared";
import { Footer } from "@lyrical-toolkit/shared";
import { Manual } from "@lyrical-toolkit/shared";
import { MusicBanner } from "@lyrical-toolkit/shared";
import { SongModal } from "@lyrical-toolkit/shared";
import { SearchTab } from "@lyrical-toolkit/shared";
import { DictionaryTab } from "@lyrical-toolkit/shared";
import { SynonymsTab } from "@lyrical-toolkit/shared";
import { RhymesTab } from "@lyrical-toolkit/shared";
import { AnalysisTab } from "@lyrical-toolkit/shared";
import { Settings } from "@lyrical-toolkit/shared";
import { UploadTab } from "@lyrical-toolkit/shared";
import { StatsTab } from "@lyrical-toolkit/shared";
import { FloatingNotepad } from "@lyrical-toolkit/shared";
import { AuthModal } from "@lyrical-toolkit/shared";
import { VerifyEmailBanner } from "@lyrical-toolkit/shared";
import { ResetPasswordModal } from "@lyrical-toolkit/shared";
import { AssignmentPopup } from "@lyrical-toolkit/shared";
import { VersionHistoryPanel } from "@lyrical-toolkit/shared";

/* eslint-disable react-hooks/exhaustive-deps */

const LyricsSearchAppContent = () => {
  const { user, isAuthenticated, logout, verifyEmail } = useAuth();

  // Email verification + password reset URL token handling.
  // Verification email link: /?verify-email-token=...
  // Password reset email link: /?reset-password-token=...
  const [resetPasswordToken, setResetPasswordToken] = useState(null);
  const [verifyStatus, setVerifyStatus] = useState(null); // null | 'success' | 'error'
  const [verifyMessage, setVerifyMessage] = useState("");

  useEffect(() => {
    if (typeof window === "undefined") return;
    const params = new URLSearchParams(window.location.search);
    const verifyToken = params.get("verify-email-token");
    const resetToken = params.get("reset-password-token");

    if (verifyToken) {
      verifyEmail(verifyToken)
        .then(() => {
          setVerifyStatus("success");
          setVerifyMessage("Your email has been verified.");
        })
        .catch((err) => {
          setVerifyStatus("error");
          setVerifyMessage(
            err.message || "This verification link is invalid or has expired.",
          );
        })
        .finally(() => {
          // Strip the token from the URL so a refresh doesn't re-trigger
          params.delete("verify-email-token");
          const newSearch = params.toString();
          window.history.replaceState(
            {},
            document.title,
            window.location.pathname + (newSearch ? "?" + newSearch : ""),
          );
        });
    }

    if (resetToken) {
      setResetPasswordToken(resetToken);
      // Strip from URL — modal owns the token in state from now on
      params.delete("reset-password-token");
      const newSearch = params.toString();
      window.history.replaceState(
        {},
        document.title,
        window.location.pathname + (newSearch ? "?" + newSearch : ""),
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Mobile detection - For web, only use screen width (no Capacitor checks)
  const [isMobile, setIsMobile] = useState(() => {
    const width = window.innerWidth;
    // Capacitor-aware: width OR running inside the Capacitor native shell.
    // window.Capacitor is defined on the web build too (as a JS bridge stub),
    // so we MUST go through isCapacitorApp(), which calls
    // window.Capacitor.isNativePlatform() to distinguish real native from web.
    const isMobileDevice = width <= 768 || isCapacitorApp();
    console.log(
      "📱 MOBILE DETECTION INITIAL: " +
        JSON.stringify({
          width,
          isCapacitor: isCapacitorApp(),
          isMobileDevice,
        }),
    );
    return isMobileDevice;
  });

  useEffect(() => {
    const handleResize = () => {
      const width = window.innerWidth;
      const isMobileDevice = width <= 768 || isCapacitorApp();
      console.log(
        "📱 RESIZE: " +
          JSON.stringify({
            width,
            isCapacitor: isCapacitorApp(),
            isMobileDevice,
          }),
      );
      setIsMobile(isMobileDevice);
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Scroll direction for collapsible header
  const scrollDirection = useScrollDirection(10);
  useScrollGlow();
  const particlesCanvasRef = useParticles();

  // Core UI state
  const [activeTab, setActiveTab] = useState("upload");
  const [selectedSong, setSelectedSong] = useState(null);

  // Song management via consolidated hook
  const {
    songs,
    setSongs,
    storageType,
    deleteSong,
    handleStorageTypeChange,
    handleTransferSong,
    saveSongsToStorage,
    saveSingleSongToStorage,
  } = useSongManager({ isAuthenticated, user });

  // Organization hook (projects, tags, filtering)
  const organization = useOrganization({
    isAuthenticated,
    user,
    storageType,
    songs,
    setSongs,
  });

  // Version history hook
  const versionHistory = useVersionHistory({ storageType, isAuthenticated });

  // Swipe gestures for mobile tab switching
  const tabOrder = ["search", "dictionary", "upload", "rhymes", "stats"];

  const handleSwipeLeft = () => {
    if (!isMobile) return;
    const currentIndex = tabOrder.indexOf(activeTab);
    // Don't swipe if we're on a tab not in the swipe order (synonyms, analysis, settings)
    if (currentIndex === -1) return;
    if (currentIndex < tabOrder.length - 1) {
      setActiveTab(tabOrder[currentIndex + 1]);
    }
  };

  const handleSwipeRight = () => {
    if (!isMobile) return;
    const currentIndex = tabOrder.indexOf(activeTab);
    // Don't swipe if we're on a tab not in the swipe order (synonyms, analysis, settings)
    if (currentIndex === -1) return;
    if (currentIndex > 0) {
      setActiveTab(tabOrder[currentIndex - 1]);
    }
  };

  useSwipeGestures(handleSwipeLeft, handleSwipeRight);

  // Dark mode locked to dark (Forest Twilight theme)
  const darkMode = true;
  const setDarkMode = () => {};

  // Word search via consolidated hook (query, history, highlight)
  // This hook owns the highlight state which API search will use
  const wordSearch = useWordSearch({
    setActiveTab,
    apiSearchHook: null, // Will be connected after apiSearch is initialized
  });
  const {
    searchQuery,
    setSearchQuery,
    searchHistory,
    highlightWord,
    setHighlightWord,
    handleSearch,
  } = wordSearch;

  // API search via consolidated hook (dictionary, synonyms, rhymes)
  const apiSearch = useAPISearch({ setHighlightWord });
  const {
    definitionQuery,
    setDefinitionQuery,
    definitionResults,
    definitionLoading,
    searchDefinition,
    synonymQuery,
    setSynonymQuery,
    synonymResults,
    synonymLoading,
    searchSynonyms,
    rhymeQuery,
    setRhymeQuery,
    rhymeResults,
    rhymeLoading,
    searchRhymes,
  } = apiSearch;

  // Enhanced searchInLyrics that combines wordSearch with apiSearch
  const searchInLyrics = useCallback(
    (word, targetTab = "search") => {
      setSearchQuery(word);
      setHighlightWord(word);
      setActiveTab(targetTab);
      wordSearch.addToSearchHistory(word);

      if (targetTab === "dictionary") {
        setDefinitionQuery(word);
        setTimeout(() => searchDefinition(word), 100);
      } else if (targetTab === "synonyms") {
        setSynonymQuery(word);
        setTimeout(() => searchSynonyms(word), 100);
      } else if (targetTab === "rhymes") {
        setRhymeQuery(word);
        setTimeout(() => searchRhymes(word), 100);
      }
    },
    [
      setSearchQuery,
      setHighlightWord,
      setActiveTab,
      wordSearch,
      setDefinitionQuery,
      searchDefinition,
      setSynonymQuery,
      searchSynonyms,
      setRhymeQuery,
      searchRhymes,
    ],
  );

  // Analysis states
  const [analysisResults, setAnalysisResults] = useState(null);
  const [analysisType, setAnalysisType] = useState(null);
  const [selectedSongForAnalysis, setSelectedSongForAnalysis] = useState(null);

  // Manual states
  const [showManual, setShowManual] = useState(false);
  // Stats filter
  const [selectedStatsFilter, setSelectedStatsFilter] = useState("all");

  // File upload hook
  const fileUploadHook = useFileUpload(songs, setSongs);
  // Search hook
  const { searchResults } = useSearch(songs, searchQuery, highlightWord);

  // Audio management via consolidated hook
  const {
    expandedAudioSongId,
    setExpandedAudioSongId,
    handleAudioUpload,
    handleAudioDownload,
    handleAudioRemove,
    triggerAudioFilePicker,
    getHiddenInputProps,
  } = useAudioManager({
    songs,
    setSongs,
    storageType,
    saveSongsToStorage,
    user,
  });

  // Loop control state for notepad audio player
  const [loopActive, setLoopActive] = useState(false);
  const [loopControls, setLoopControls] = useState(null);
  // Loop-record active in UploadTab (suppresses FloatingNotepad save-take dialog)
  const [isLoopRecordActive, setIsLoopRecordActive] = useState(false);
  // Takes panel open in UploadTab (suppresses FloatingNotepad save-take dialog)
  const [isTakesRecordActive, setIsTakesRecordActive] = useState(false);

  // Recording hooks
  const recorder = useAudioRecorder({
    durationLimitSec: organization.recordingDurationSec,
  });
  const takeManager = useTakeManager({
    storageType,
    userId: user?.id,
    isAuthenticated,
  });

  // Teleprompter state
  const [teleprompterSongId, setTeleprompterSongId] = useState(null);
  const teleprompterSong = useMemo(
    () => songs.find((s) => s.id === teleprompterSongId) || null,
    [songs, teleprompterSongId],
  );
  // Persist teleprompter config back to song object
  const handleTeleprompterConfigChange = useCallback(
    (config) => {
      if (!teleprompterSong) return;
      const updatedSongs = songs.map((s) =>
        s.id === teleprompterSong.id
          ? { ...s, teleprompterConfig: config }
          : s,
      );
      setSongs(updatedSongs);
      saveSongsToStorage(updatedSongs);
    },
    [teleprompterSong, songs, setSongs, saveSongsToStorage],
  );

  const teleprompter = useTeleprompter({
    song: teleprompterSong,
    onConfigChange: handleTeleprompterConfigChange,
  });

  const handleOpenTeleprompter = useCallback(
    (song) => {
      setTeleprompterSongId(teleprompterSongId === song.id ? null : song.id);
    },
    [teleprompterSongId],
  );

  // Save take handler — bridges recorder → takeManager
  const handleSaveTake = useCallback(
    async (songId, blob, label, duration) => {
      await takeManager.saveTake(songId, blob, label, duration);
    },
    [takeManager],
  );

  // Toggle recording for notepad integration
  const handleNotepadToggleRecording = useCallback(() => {
    if (recorder.isRecording) {
      recorder.stopRecording();
    } else {
      recorder.startRecording();
    }
  }, [recorder]);

  // Notepad/Editor via consolidated hook (includes drafts, tabs, auto-save)
  const notepadEditor = useNotepadEditor({
    songs,
    setSongs,
    storageType,
    isAuthenticated,
    saveSongsToStorage,
    saveSingleSongToStorage,
  });
  const {
    // Base notepad state
    content: notepadContent,
    title: notepadTitle,
    updateContent: updateNotepadContent,
    updateTitle: updateNotepadTitle,
    isMinimized: notepadIsMinimized,
    toggleMinimized: toggleNotepadMinimized,
    dimensions: notepadDimensions,
    updateDimensions: updateNotepadDimensions,
    position: notepadPosition,
    setPosition: setNotepadPosition,
    currentEditingSongId,
    setCurrentEditingSongId,
    // Editor-specific state
    originalSongContent,
    hasUnsavedChanges,
    // Tab management
    openTabs,
    activeTabIndex,
    MAX_DRAFTS_PER_SONG,
    // Notepad action handlers
    handleExportTxt,
    handleUploadToSongs,
    handleSaveChanges,
    handleRevertChanges,
    handleStartNewContent,
    handleEditSong,
    // Draft handlers
    handleCreateDraft,
    handleDeleteDraft,
    handleOpenDraft,
    handleSwitchTab,
    handleCloseTab,
    getTabName,
  } = notepadEditor;

  // Create a notepadState-like object for FloatingNotepad compatibility
  const notepadState = {
    content: notepadContent,
    title: notepadTitle,
    updateContent: updateNotepadContent,
    updateTitle: updateNotepadTitle,
    isMinimized: notepadIsMinimized,
    toggleMinimized: toggleNotepadMinimized,
    dimensions: notepadDimensions,
    updateDimensions: updateNotepadDimensions,
    position: notepadPosition,
    setPosition: setNotepadPosition,
    currentEditingSongId,
    setCurrentEditingSongId,
  };

  const [showAuthModal, setShowAuthModal] = useState(false);

  // Auto-close auth modal when user becomes authenticated (e.g. Google OAuth callback)
  useEffect(() => {
    if (isAuthenticated) {
      setShowAuthModal(false);
    }
  }, [isAuthenticated]);

  // Organization modal state
  const [showAssignmentPopup, setShowAssignmentPopup] = useState(false);
  const [assignmentSong, setAssignmentSong] = useState(null);

  const handleAssignSong = (song) => {
    setAssignmentSong(song);
    setShowAssignmentPopup(true);
  };

  // Version history handlers
  const handleOpenVersionPanel = useCallback(
    (song) => {
      versionHistory.openVersionPanel(song.id);
    },
    [versionHistory],
  );

  const handleSelectVersion = useCallback(
    async (version) => {
      const songId = versionHistory.versionPanelSongId;
      if (!songId) return;
      versionHistory.setSelectedVersion(version);
      const versionNumber = version.versionNumber || version.version_number;
      await versionHistory.loadVersionContent(songId, versionNumber);

      // Load previous version content for diff view
      const vList = versionHistory.versions;
      const idx = vList.findIndex(
        (v) => (v.versionNumber || v.version_number) === versionNumber,
      );
      const prevVersion = idx >= 0 ? vList[idx + 1] : null;
      if (prevVersion) {
        const prevNum =
          prevVersion.versionNumber || prevVersion.version_number;
        const prevContent = await versionHistory.fetchVersionContent(
          songId,
          prevNum,
        );
        versionHistory.setPreviousVersionContent(prevContent);
      } else {
        versionHistory.setPreviousVersionContent(null);
      }
    },
    [versionHistory],
  );

  const handleVersionRestore = useCallback(
    async (versionNumber) => {
      const songId = versionHistory.versionPanelSongId;
      if (!songId) return;

      const currentSong = songs.find((s) => s.id === songId);
      if (!currentSong) return;

      try {
        const restoredContent = await versionHistory.restoreVersion(
          songId,
          versionNumber,
          currentSong.title,
          currentSong.lyrics,
        );

        if (restoredContent) {
          const updatedSongs = songs.map((s) =>
            s.id === songId
              ? {
                  ...s,
                  lyrics: restoredContent,
                  content: restoredContent,
                  wordCount: restoredContent
                    .split(/\s+/)
                    .filter((w) => w.trim()).length,
                  lineCount: restoredContent.split("\n").filter((l) => l.trim())
                    .length,
                  dateModified: new Date().toISOString(),
                }
              : s,
          );
          setSongs(updatedSongs);
          await saveSongsToStorage(updatedSongs);

          // If this song is currently being edited in notepad, update notepad
          if (currentEditingSongId === songId) {
            updateNotepadContent(restoredContent);
          }
        }
      } catch (error) {
        console.error("❌ Failed to restore version:", error);
        alert("Failed to restore version. Please try again.");
      }
    },
    [
      songs,
      setSongs,
      saveSongsToStorage,
      versionHistory,
      currentEditingSongId,
      updateNotepadContent,
    ],
  );

  // Wrap handleSaveChanges to also create a version snapshot
  const handleSaveChangesWithVersioning = useCallback(async () => {
    await handleSaveChanges();
    if (currentEditingSongId && notepadContent) {
      try {
        await versionHistory.maybeCreateVersion(
          currentEditingSongId,
          notepadTitle,
          notepadContent,
        );
      } catch (error) {
        console.error(
          "⚠️ Version creation failed (save still succeeded):",
          error,
        );
      }
    }
  }, [
    handleSaveChanges,
    currentEditingSongId,
    notepadContent,
    notepadTitle,
    versionHistory,
  ]);

  // Init version tracking when a song is opened for editing
  useEffect(() => {
    if (currentEditingSongId) {
      const song = songs.find((s) => s.id === currentEditingSongId);
      if (song) {
        versionHistory.initSongVersionTracking(
          currentEditingSongId,
          song.lyrics,
        );
      }
    }
  }, [currentEditingSongId]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleLogout = async () => {
    await logout();
    setSongs([]);
    setShowAuthModal(false);
  };

  // Reset stats filter when songs change (keep this, UI-only logic)
  useEffect(() => {
    if (songs.length === 0) {
      setSelectedStatsFilter("all");
    } else if (selectedStatsFilter !== "all") {
      const selectedSongExists = songs.some(
        (song) => song.id.toString() === selectedStatsFilter,
      );
      if (!selectedSongExists) {
        setSelectedStatsFilter("all");
      }
    }
  }, [songs, selectedStatsFilter]);

  // Debug token expiration issues
  useEffect(() => {
    if (!isAuthenticated) return;

    const checkTokenHealth = () => {
      const token = authService.getAccessToken();
      if (token) {
        try {
          const payload = JSON.parse(atob(token.split(".")[1]));
          const now = Date.now() / 1000;
          const timeUntilExpiry = payload.exp - now;

          if (timeUntilExpiry < 300) {
            // Less than 5 minutes
            console.log(
              `⚠️ Token expires in ${Math.floor(timeUntilExpiry)} seconds`,
            );
          }
        } catch (error) {
          console.error("Failed to parse token:", error);
        }
      }
    };

    // Check token health every 5 minutes (reduced from 1 minute to prevent memory buildup)
    const interval = setInterval(checkTokenHealth, 300000);
    return () => clearInterval(interval);
  }, [isAuthenticated]);

  // Enhanced statistics with song filtering
  const stats = useMemo(() => {
    const filteredSongs =
      selectedStatsFilter === "all"
        ? songs
        : songs.filter((song) => song.id.toString() === selectedStatsFilter);

    if (filteredSongs.length === 0) {
      return {
        totalSongs: 0,
        totalWords: 0,
        uniqueWords: 0,
        mostUsedWords: [],
        syllableDistribution: {},
        wordLengthDistribution: {},
        averageWordsPerSong: 0,
        averageLinesPerSong: 0,
        averageWordLength: 0,
        averageSyllablesPerWord: 0,
        totalLines: 0,
        readingLevel: 0,
        vocabularyComplexity: 0,
        rhymeStats: {
          totalRhymableWords: 0,
          perfectRhymes: 0,
          nearRhymes: 0,
          soundsLike: 0,
          internalRhymes: 0,
          rhymeDensity: 0,
          allRhymeGroups: [],
        },
      };
    }

    // Calculate basic metrics first
    const totalWords = filteredSongs.reduce(
      (sum, song) => sum + song.wordCount,
      0,
    );
    const allWords = filteredSongs.flatMap((song) =>
      song.lyrics
        .toLowerCase()
        .split(/\s+/)
        .filter((word) => word.match(/[a-zA-Z]/)),
    );

    const wordFreq = {};
    const syllableCount = {};
    const wordLengthCount = {};
    let totalSyllables = 0;
    let totalCharacters = 0;
    let validWordCount = 0;

    allWords.forEach((word) => {
      const cleanWord = word.replace(/[^\w]/g, "");
      if (cleanWord.length > 0) {
        validWordCount++;
        totalCharacters += cleanWord.length;

        if (cleanWord.length > 2) {
          wordFreq[cleanWord] = (wordFreq[cleanWord] || 0) + 1;
        }

        const syllables = countSyllables(cleanWord);
        totalSyllables += syllables;
        const syllableKey = syllables > 4 ? "5+" : syllables.toString();
        syllableCount[syllableKey] = (syllableCount[syllableKey] || 0) + 1;

        const lengthKey =
          cleanWord.length > 10 ? "11+" : cleanWord.length.toString();
        wordLengthCount[lengthKey] = (wordLengthCount[lengthKey] || 0) + 1;
      }
    });

    const mostUsedWords = Object.entries(wordFreq)
      .sort(([, a], [, b]) => b - a)
      .slice(0, 10);

    const allLines = filteredSongs.flatMap((song) =>
      song.lyrics.split("\n").filter((line) => line.trim().length > 0),
    );
    const totalLines = allLines.length;

    const avgReadingLevel =
      filteredSongs.length > 0
        ? filteredSongs.reduce(
            (sum, song) => sum + calculateReadingLevel(song.lyrics),
            0,
          ) / filteredSongs.length
        : 0;

    const avgVocabularyComplexity =
      filteredSongs.length > 0
        ? filteredSongs.reduce(
            (sum, song) =>
              sum + calculateVocabularyComplexity(song.lyrics, wordFreq),
            0,
          ) / filteredSongs.length
        : 0;

    // Calculate rhyme statistics
    const combinedRhymeStats =
      filteredSongs.length > 0
        ? filteredSongs.reduce(
            (acc, song) => {
              const songRhymes = analyzeRhymeStatistics(
                song.lyrics,
                songVocabularyPhoneticMap,
              );
              return {
                totalRhymableWords:
                  acc.totalRhymableWords + songRhymes.totalRhymableWords,
                perfectRhymes: acc.perfectRhymes + songRhymes.perfectRhymes,
                nearRhymes: acc.nearRhymes + songRhymes.nearRhymes,
                soundsLike: acc.soundsLike + songRhymes.soundsLike,
                internalRhymes: acc.internalRhymes + songRhymes.internalRhymes,
                rhymeDensity: acc.rhymeDensity + songRhymes.rhymeDensity,
                allRhymeGroups: [
                  ...acc.allRhymeGroups,
                  ...songRhymes.rhymeGroups,
                ],
              };
            },
            {
              totalRhymableWords: 0,
              perfectRhymes: 0,
              nearRhymes: 0,
              soundsLike: 0,
              internalRhymes: 0,
              rhymeDensity: 0,
              allRhymeGroups: [],
            },
          )
        : {
            totalRhymableWords: 0,
            perfectRhymes: 0,
            nearRhymes: 0,
            soundsLike: 0,
            internalRhymes: 0,
            rhymeDensity: 0,
            allRhymeGroups: [],
          };

    if (filteredSongs.length > 1) {
      combinedRhymeStats.rhymeDensity =
        combinedRhymeStats.rhymeDensity / filteredSongs.length;
    }

    return {
      totalSongs: filteredSongs.length,
      totalWords,
      uniqueWords: Object.keys(wordFreq).length,
      mostUsedWords,
      syllableDistribution: syllableCount,
      wordLengthDistribution: wordLengthCount,
      averageWordsPerSong:
        filteredSongs.length > 0
          ? Math.round(totalWords / filteredSongs.length)
          : 0,
      averageLinesPerSong:
        filteredSongs.length > 0
          ? Math.round(totalLines / filteredSongs.length)
          : 0,
      averageWordLength:
        validWordCount > 0 ? (totalCharacters / validWordCount).toFixed(1) : 0,
      averageSyllablesPerWord:
        validWordCount > 0 ? (totalSyllables / validWordCount).toFixed(1) : 0,
      totalLines,
      readingLevel: avgReadingLevel,
      vocabularyComplexity: avgVocabularyComplexity,
      rhymeStats: combinedRhymeStats,
    };
  }, [songs, selectedStatsFilter]);

  // Export song as TXT file (needed for UploadTab)
  const handleExportSongTxt = async (song) => {
    try {
      const result = await downloadFile(
        song.lyrics,
        `${song.title}.txt`,
        "text/plain",
      );
      if (result?.path) alert(`Saved to Documents/${song.title}.txt`);
    } catch (error) {
      console.error("Error exporting TXT:", error);
      alert("Error exporting file. Please try again.");
    }
  };

  // Export song as PDF file (needed for UploadTab)
  const handleExportSongPdf = async (song) => {
    try {
      const pdf = new jsPDF();
      pdf.setFontSize(16);
      pdf.text(song.title, 20, 20);
      pdf.setFontSize(12);
      const splitText = pdf.splitTextToSize(song.lyrics, 170);
      pdf.text(splitText, 20, 40);
      const pdfBlob = pdf.output("blob");
      const result = await downloadFile(
        pdfBlob,
        `${song.title}.pdf`,
        "application/pdf",
      );
      if (result?.path) alert(`Saved to Documents/${song.title}.pdf`);
    } catch (error) {
      console.error("Error generating PDF:", error);
      alert("Error generating PDF. Please try again.");
    }
  };

  return (
    <div
      className="min-h-screen radial-bg-forest forest-theme text-forest-100"
      style={{ display: "flex", flexDirection: "column" }}
    >
      {/* Background particles - desktop only */}
      <canvas
        ref={particlesCanvasRef}
        style={{
          position: "fixed",
          top: 0,
          left: 0,
          width: "100%",
          height: "100%",
          pointerEvents: "none",
          zIndex: 0,
        }}
      />

      {/* Hidden audio file input for direct upload */}
      <input {...getHiddenInputProps()} />

      {/* Music Banner - hidden for pro subscribers */}
      <MusicBanner user={user} />

      {/* Verification status flash (shown after clicking the email link) */}
      {verifyStatus && (
        <div
          className={`text-sm border-b px-4 py-2 text-center ${
            verifyStatus === "success"
              ? "bg-leaf-500/15 border-leaf-500/40 text-leaf-200"
              : "bg-ember-500/15 border-ember-500/40 text-ember-200"
          }`}
        >
          <span className="mr-3">{verifyMessage}</span>
          <button
            onClick={() => setVerifyStatus(null)}
            className="underline hover:opacity-80"
          >
            Dismiss
          </button>
        </div>
      )}

      {/* Persistent banner prompting unverified users to confirm their email */}
      <VerifyEmailBanner />

      {/* Header - Hide on mobile when scrolling down */}
      <div
        className={`transition-transform duration-300 ${
          isMobile && scrollDirection === "down"
            ? "-translate-y-full"
            : "translate-y-0"
        }`}
      >
        <Header
          activeTab={activeTab}
          setActiveTab={setActiveTab}
          showManual={showManual}
          setShowManual={setShowManual}
          darkMode={darkMode}
          setDarkMode={setDarkMode}
          isAuthenticated={isAuthenticated}
          user={user}
          onLogin={() => setShowAuthModal(true)}
          onLogout={handleLogout}
          onOpenAISettings={() => setActiveTab("settings")}
        />
      </div>

      <AuthModal
        isOpen={showAuthModal}
        onClose={() => setShowAuthModal(false)}
        darkMode={darkMode}
      />

      {/* Password reset modal — shown when arriving from a reset email link */}
      <ResetPasswordModal
        token={resetPasswordToken}
        onClose={() => setResetPasswordToken(null)}
      />

      {/* Universal Search Bar */}
      {!["upload", "stats", "analysis", "settings"].includes(activeTab) &&
        !showManual && (
          <div className="w-full max-w-6xl mx-auto px-4 py-4">
            <div className="relative mobile-search">
              {activeTab === "search" && (
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-forest-300 w-5 h-5 pointer-events-none" />
              )}
              {activeTab === "dictionary" && (
                <Book className="absolute left-3 top-1/2 transform -translate-y-1/2 text-forest-300 w-5 h-5 pointer-events-none" />
              )}
              {activeTab === "synonyms" && (
                <Shuffle className="absolute left-3 top-1/2 transform -translate-y-1/2 text-forest-300 w-5 h-5 pointer-events-none" />
              )}
              {activeTab === "rhymes" && (
                <Music className="absolute left-3 top-1/2 transform -translate-y-1/2 text-forest-300 w-5 h-5 pointer-events-none" />
              )}

              <input
                type="text"
                placeholder={
                  activeTab === "search"
                    ? isMobile
                      ? 'Search lyrics ("quotes" for exact)'
                      : 'Search lyrics... (use "quotes" for exact)'
                    : activeTab === "dictionary"
                      ? isMobile
                        ? "Define a Word"
                        : "Enter a word to get its definition..."
                      : activeTab === "synonyms"
                        ? "Find synonyms and antonyms..."
                        : activeTab === "rhymes"
                          ? "Find words that rhyme"
                          : ""
                }
                value={
                  activeTab === "search"
                    ? searchQuery
                    : activeTab === "dictionary"
                      ? definitionQuery
                      : activeTab === "synonyms"
                        ? synonymQuery
                        : activeTab === "rhymes"
                          ? rhymeQuery
                          : ""
                }
                onChange={(e) => {
                  if (activeTab === "search") handleSearch(e.target.value);
                  else if (activeTab === "dictionary")
                    setDefinitionQuery(e.target.value);
                  else if (activeTab === "synonyms")
                    setSynonymQuery(e.target.value);
                  else if (activeTab === "rhymes")
                    setRhymeQuery(e.target.value);
                }}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    if (activeTab === "dictionary")
                      searchDefinition(definitionQuery);
                    else if (activeTab === "synonyms")
                      searchSynonyms(synonymQuery);
                    else if (activeTab === "rhymes") searchRhymes(rhymeQuery);
                  }
                }}
                className={`w-full pl-10 ${activeTab !== "search" ? "pr-24" : "pr-4"} py-3 border rounded-lg focus:ring-2 focus:ring-gold-500 focus:border-transparent transition-colors bg-forest-800 border-forest-600 text-forest-100 placeholder-forest-300`}
              />

              {/* Inline search button - inside input, right side */}
              {activeTab !== "search" && (
                <button
                  onClick={() => {
                    if (activeTab === "dictionary")
                      searchDefinition(definitionQuery);
                    else if (activeTab === "synonyms")
                      searchSynonyms(synonymQuery);
                    else if (activeTab === "rhymes") searchRhymes(rhymeQuery);
                  }}
                  disabled={
                    (activeTab === "dictionary" && definitionLoading) ||
                    (activeTab === "synonyms" && synonymLoading) ||
                    (activeTab === "rhymes" && rhymeLoading)
                  }
                  style={{
                    position: "absolute",
                    right: "8px",
                    top: "50%",
                    transform: "translateY(-50%)",
                  }}
                  className="px-4 py-1.5 rounded-md transition-colors bg-gold-500 hover:bg-gold-400 text-forest-950 disabled:bg-forest-700 disabled:text-forest-300 font-medium text-sm"
                >
                  {(activeTab === "dictionary" && definitionLoading) ||
                  (activeTab === "synonyms" && synonymLoading) ||
                  (activeTab === "rhymes" && rhymeLoading)
                    ? "..."
                    : activeTab === "dictionary"
                      ? "Define"
                      : activeTab === "synonyms"
                        ? "Search"
                        : activeTab === "rhymes"
                          ? "Find"
                          : "Search"}
                </button>
              )}
            </div>

            {/* Search History - only show for main search tab */}
            {activeTab === "search" && searchHistory.length > 0 && (
              <div className="mt-3 flex flex-wrap gap-2">
                <span className="text-sm text-forest-300">Recent:</span>
                {searchHistory.slice(0, 5).map((term, index) => (
                  <button
                    key={index}
                    onClick={() => handleSearch(term)}
                    className="text-sm px-3 py-1 rounded-full transition-colors bg-forest-700 text-forest-200 hover:bg-forest-600"
                  >
                    {term}
                  </button>
                ))}
              </div>
            )}
          </div>
        )}

      {/* Main Content - Add bottom padding on mobile for bottom nav and notepad */}
      <div
        className="w-full max-w-6xl mx-auto px-4 py-6 mobile-content"
        style={{
          paddingBottom: isMobile
            ? notepadState.isMinimized
              ? "calc(15rem + env(safe-area-inset-bottom, 0px))"
              : `calc(15rem + ${notepadState.dimensions.height}px + env(safe-area-inset-bottom, 0px))`
            : "20rem",
        }}
      >
        {/* Manual Content */}
        <Manual
          showManual={showManual}
          onClose={() => setShowManual(false)}
          darkMode={darkMode}
        />

        {/* Tab Content - Only show when manual is closed */}
        {!showManual && (
          <>
            {activeTab === "search" && (
              <SearchTab
                searchQuery={searchQuery}
                highlightWord={highlightWord}
                searchResults={searchResults}
                songs={songs}
                stats={stats}
                onSongSelect={setSelectedSong}
                darkMode={darkMode}
              />
            )}

            {activeTab === "dictionary" && (
              <DictionaryTab
                definitionResults={definitionResults}
                definitionLoading={definitionLoading}
                definitionQuery={definitionQuery}
                onSearchInLyrics={searchInLyrics}
                darkMode={darkMode}
              />
            )}

            {activeTab === "synonyms" && (
              <SynonymsTab
                synonymResults={synonymResults}
                synonymLoading={synonymLoading}
                synonymQuery={synonymQuery}
                onSearchInLyrics={searchInLyrics}
                darkMode={darkMode}
              />
            )}

            {activeTab === "rhymes" && (
              <RhymesTab
                rhymeResults={rhymeResults}
                rhymeLoading={rhymeLoading}
                rhymeQuery={rhymeQuery}
                onSearchInLyrics={searchInLyrics}
                darkMode={darkMode}
              />
            )}

            {activeTab === "analysis" && (
              <AnalysisTab
                songs={songs}
                selectedSongForAnalysis={selectedSongForAnalysis}
                setSelectedSongForAnalysis={setSelectedSongForAnalysis}
                analysisResults={analysisResults}
                setAnalysisResults={setAnalysisResults}
                analysisType={analysisType}
                setAnalysisType={setAnalysisType}
                onSearchInLyrics={searchInLyrics}
                stats={stats}
              />
            )}

            {activeTab === "settings" && (
              <Settings
                darkMode={darkMode}
                user={user}
                isAuthenticated={isAuthenticated}
                onLogout={handleLogout}
              />
            )}

            <div style={{ display: activeTab === "upload" ? "block" : "none" }}>
              <UploadTab
                songs={organization.getFilteredSongs(songs)}
                onFileUpload={fileUploadHook.handleFileUpload}
                onDeleteSong={deleteSong}
                onSongSelect={setSelectedSong}
                onEditSong={handleEditSong}
                onExportTxt={handleExportSongTxt}
                onExportPdf={handleExportSongPdf}
                isDragging={fileUploadHook.isDragging}
                handleDragOver={fileUploadHook.handleDragOver}
                handleDragLeave={fileUploadHook.handleDragLeave}
                handleDrop={fileUploadHook.handleDrop}
                darkMode={darkMode}
                // Audio-related props
                onAudioUpload={handleAudioUpload}
                onAudioDownload={handleAudioDownload}
                onAudioRemove={handleAudioRemove}
                userId={user?.id || "anonymous"}
                // Storage type props
                storageType={storageType}
                onStorageTypeChange={handleStorageTypeChange}
                isAuthenticated={isAuthenticated}
                onTransferSong={handleTransferSong}
                // Draft-related props
                onCreateDraft={handleCreateDraft}
                onDeleteDraft={handleDeleteDraft}
                onOpenDraft={handleOpenDraft}
                MAX_DRAFTS_PER_SONG={MAX_DRAFTS_PER_SONG}
                // Audio player expansion state (persistent across tab switches)
                expandedAudioSongId={expandedAudioSongId}
                setExpandedAudioSongId={setExpandedAudioSongId}
                // Unsaved changes warning
                hasUnsavedChanges={hasUnsavedChanges}
                // Organization props
                projects={organization.projects}
                tags={organization.tags}
                activeProjectId={organization.activeProjectFilter}
                activeTagIds={organization.activeTagFilters}
                songCounts={organization.songCounts}
                totalSongCount={songs.filter((s) => !s.isExample).length}
                onProjectFilter={organization.setProjectFilter}
                onTagFilter={organization.toggleTagFilter}
                onClearFilters={organization.clearFilters}
                onAssignSong={handleAssignSong}
                onOpenVersionPanel={handleOpenVersionPanel}
                // Recording props
                recorder={recorder}
                takeManager={takeManager}
                onSaveTake={handleSaveTake}
                isMobile={isMobile}
                // Teleprompter
                onOpenTeleprompter={handleOpenTeleprompter}
                teleprompterSongId={teleprompterSongId}
                // Pro features (punch-in, etc.)
                isPro={true}
                onLoopRecordActiveChange={setIsLoopRecordActive}
                onTakesRecordActiveChange={setIsTakesRecordActive}
              />
            </div>

            {activeTab === "stats" && (
              <StatsTab
                songs={songs}
                stats={stats}
                selectedStatsFilter={selectedStatsFilter}
                setSelectedStatsFilter={setSelectedStatsFilter}
                onSearchInLyrics={(word) => {
                  handleSearch(word);
                  setActiveTab("search");
                }}
                darkMode={darkMode}
              />
            )}
          </>
        )}
      </div>

      {/* Song Modal */}
      <SongModal
        selectedSong={selectedSong}
        onClose={() => setSelectedSong(null)}
        highlightWord={highlightWord}
        darkMode={darkMode}
      />

      {/* Organization Modals */}
      <AssignmentPopup
        isOpen={showAssignmentPopup}
        onClose={() => {
          setShowAssignmentPopup(false);
          setAssignmentSong(null);
        }}
        song={assignmentSong}
        projects={organization.projects}
        tags={organization.tags}
        onUpdateOrganization={organization.updateSongOrganization}
        onCreateProject={organization.createProject}
        onCreateTag={organization.createTag}
        onDeleteProject={organization.deleteProject}
        onDeleteTag={organization.deleteTag}
        canCreateMoreProjects={organization.canCreateMoreProjects}
        canCreateMoreTags={organization.canCreateMoreTags}
        isMobile={isMobile}
      />

      {/* Version History Panel */}
      <VersionHistoryPanel
        isOpen={!!versionHistory.versionPanelSongId}
        onClose={versionHistory.closeVersionPanel}
        songTitle={
          songs.find((s) => s.id === versionHistory.versionPanelSongId)
            ?.title || ""
        }
        versions={versionHistory.versions}
        selectedVersion={versionHistory.selectedVersion}
        onSelectVersion={handleSelectVersion}
        selectedVersionContent={versionHistory.selectedVersionContent}
        previousVersionContent={versionHistory.previousVersionContent}
        isLoading={versionHistory.isLoadingVersions}
        onRestore={handleVersionRestore}
        isMobile={isMobile}
      />

      {/* Teleprompter (fullscreen portal at z-60) */}
      <TeleprompterView
        song={teleprompterSong}
        isOpen={!!teleprompterSongId}
        onClose={() => setTeleprompterSongId(null)}
        isMobile={isMobile}
        recorder={recorder}
        isPro={true}
        teleprompter={teleprompter}
        takeManager={takeManager}
        onSaveTake={handleSaveTake}
      />

      {/* Add the FloatingNotepad here — hidden when teleprompter is open */}
      {!teleprompterSongId && (
        <FloatingNotepad
          notepadState={notepadState}
          darkMode={darkMode}
          onExportTxt={handleExportTxt}
          onUploadToSongs={handleUploadToSongs}
          onSaveChanges={handleSaveChangesWithVersioning}
          onRevertChanges={handleRevertChanges}
          onStartNewContent={handleStartNewContent}
          hasUnsavedChanges={hasUnsavedChanges}
          originalSongContent={originalSongContent}
          // Audio-related props
          currentSongAudio={(() => {
            const currentSong = songs.find(
              (s) => s.id === notepadState.currentEditingSongId,
            );
            return currentSong && currentSong.audioFileUrl
              ? {
                  url: currentSong.audioFileUrl,
                  filename: currentSong.audioFileName,
                  size: currentSong.audioFileSize,
                  duration: currentSong.audioDuration,
                }
              : null;
          })()}
          onAudioDownload={() => {
            const currentSong = songs.find(
              (s) => s.id === notepadState.currentEditingSongId,
            );
            if (currentSong) handleAudioDownload(currentSong);
          }}
          onAudioRemove={() => {
            if (notepadState.currentEditingSongId)
              handleAudioRemove(notepadState.currentEditingSongId);
          }}
          onAudioReplace={() => {
            if (notepadState.currentEditingSongId) {
              triggerAudioFilePicker(notepadState.currentEditingSongId);
            }
          }}
          // Loop control props (replaces tabs when loop is active)
          loopActive={loopActive}
          loopControls={loopControls}
          onLoopStateChange={setLoopActive}
          onLoopControlsReady={setLoopControls}
          // Recording props — only when a saved song is loaded
          onToggleRecording={
            notepadState.currentEditingSongId
              ? handleNotepadToggleRecording
              : null
          }
          isRecording={recorder.isRecording}
          currentSongId={notepadState.currentEditingSongId}
          audioBlob={recorder.audioBlob}
          recordingDuration={recorder.recordingDuration}
          onSaveTake={handleSaveTake}
          onResetRecording={recorder.resetRecording}
          isLoopRecordActive={isLoopRecordActive}
          teleprompterSections={(() => {
            const currentSong = songs.find(
              (s) => s.id === notepadState.currentEditingSongId,
            );
            return currentSong?.teleprompterConfig?.sections || null;
          })()}
        />
      )}

      {/* Footer with legal links */}
      <Footer isMobile={isMobile} />

      {/* Bottom Navigation - Mobile Only */}
      {(() => {
        console.log(
          "🔍 BottomNav render: " + JSON.stringify({ isMobile, activeTab }),
        );
        return isMobile ? (
          <BottomNav
            activeTab={activeTab}
            setActiveTab={setActiveTab}
            darkMode={darkMode}
          />
        ) : null;
      })()}
    </div>
  );
};

// Wrapper component with AuthProvider
const LyricsSearchApp = () => {
  return (
    <AuthProvider>
      <LyricsSearchAppContent />
    </AuthProvider>
  );
};

export default LyricsSearchApp;
